import apiClient, { SESSION_CHECK_PATH } from '../lib/apiClient';
import { buildTaskAudioUrl } from './taskAudioUrl';

const extractData = (promise) => promise.then((response) => response.data);
const TASKS_SEARCH_TIMEOUT_MS = 120000;
const LOGOUT_TIMEOUT_MS = 5000;

export const fetchCurrentUser = () => extractData(apiClient.get(SESSION_CHECK_PATH));

export const logoutSession = () => apiClient.post('/auth/logout', null, { timeout: LOGOUT_TIMEOUT_MS });

export const fetchDashboardKpi = (params) =>
  extractData(apiClient.get('/analytics/dashboard/kpi', { params }));

export const fetchCallDynamics = (params) =>
  extractData(apiClient.get('/analytics/dashboard/call-dynamics', { params }));

export const fetchComplaintsNegativity = (params) =>
  extractData(apiClient.get('/analytics/dashboard/complaints-negativity', { params }));

export const fetchCallTopics = (params) =>
  extractData(apiClient.get('/analytics/dashboard/call-topics', { params }));

export const fetchElectrobikeFunnel = (params) =>
  extractData(apiClient.get('/analytics/dashboard/electrobike-funnel', { params }));

export const fetchObjections = (params) =>
  extractData(apiClient.get('/analytics/dashboard/objections', { params }));

export const fetchObjectionExamples = (params) =>
  extractData(apiClient.get('/analytics/dashboard/objections/examples', { params }));

export const fetchConversionDynamics = (params) =>
  extractData(apiClient.get('/analytics/dashboard/conversion-dynamics', { params }));

export const fetchDomainConversion = (params) =>
  extractData(apiClient.get('/analytics/dashboard/domain-conversion', { params }));

export const fetchProgramInterestGaps = (params) =>
  extractData(apiClient.get('/analytics/dashboard/program-interest-gaps', { params }));

export const fetchEmployeeKpi = (params) =>
  extractData(apiClient.get('/analytics/employees/kpi', { params }));

export const fetchEmployeeEfficiency = (params) =>
  extractData(apiClient.get('/analytics/employees/efficiency', { params }));

export const fetchEmployeeDailyWorkload = (params) =>
  extractData(apiClient.get('/analytics/employees/workload/daily', { params }));

export const fetchEmployeeHourlyWorkload = (params) =>
  extractData(apiClient.get('/analytics/employees/workload/hourly', { params }));

export const fetchEmployeeHourlyWorkloadByDay = (params) =>
  extractData(apiClient.get('/analytics/employees/workload/hourly-by-day', { params }));

export const fetchEmployeeHourlyWaitWorkload = (params) =>
  extractData(apiClient.get('/analytics/employees/workload/hourly-wait', { params }));

export const fetchEmployeeCallsDynamics = (params) =>
  extractData(apiClient.get('/analytics/employees/calls-dynamics', { params }));

export const fetchEmployeeQualityTrend = (params) =>
  extractData(apiClient.get('/analytics/employees/quality-trend', { params }));

// Фильтр сотрудников и отделов сужает столбцы, но не среднее по всем сотрудникам.
export const fetchEmployeeQualityComparison = (params) =>
  extractData(apiClient.get('/analytics/employees/quality-comparison', { params }));

/** Формы чек-листа активного пакета узлов для селектора «Динамики качества работы». */
export const fetchEmployeeQualityForms = () =>
  extractData(apiClient.get('/analytics/employees/quality-forms'));

export const fetchEmployeeBookingFunnel = (params) =>
  extractData(apiClient.get('/analytics/employees/booking-funnel', { params }));

export const fetchEmployeeForbiddenPhrases = (params) =>
  extractData(apiClient.get('/analytics/employees/forbidden-phrases', { params }));

export const fetchChecklistCriteria = (params) =>
  extractData(apiClient.get('/analytics/employees/checklist-criteria', { params }));

export const fetchChecklistHeatmap = (params) =>
  extractData(apiClient.get('/analytics/employees/checklist-heatmap', { params }));

export const fetchChecklistExamples = (params) =>
  extractData(apiClient.get('/analytics/employees/checklist-examples', { params }));

export const fetchEmployees = (params) =>
  extractData(apiClient.get('/employees', { params }));

export const restoreEmployeesFromHistory = () =>
  extractData(apiClient.post('/employees/restore-from-history'));

export const fetchCallNumberFilters = () =>
  extractData(apiClient.get('/call-number-filters'));

export const createCallNumberFilter = (payload) =>
  extractData(apiClient.post('/call-number-filters', payload));

export const deleteCallNumberFilter = (id) =>
  extractData(apiClient.delete(`/call-number-filters/${id}`));

export const fetchCalls = (params) =>
  extractData(apiClient.get('/analytics/calls', { params }));

export const fetchMissedCalls = (params) =>
  extractData(apiClient.get('/missed-calls', { params }));

export const fetchMissedCallOrders = (payload) =>
  extractData(apiClient.post('/missed-calls/orders/lookup', payload));

export const refreshMissedCallOrder = (payload) =>
  extractData(apiClient.post('/missed-calls/orders/refresh', payload));

export const claimMissedCall = (clientPhone, params) =>
  extractData(apiClient.post(`/missed-calls/groups/${encodeURIComponent(clientPhone)}/claim`, null, { params }));

export const unclaimMissedCall = (clientPhone, params) =>
  extractData(apiClient.post(`/missed-calls/groups/${encodeURIComponent(clientPhone)}/unclaim`, null, { params }));

export const markMissedCallProcessed = (clientPhone, params) =>
  extractData(apiClient.post(`/missed-calls/groups/${encodeURIComponent(clientPhone)}/mark-processed`, null, { params }));

export const unmarkMissedCallProcessed = (clientPhone, params) =>
  extractData(apiClient.post(`/missed-calls/groups/${encodeURIComponent(clientPhone)}/unmark-processed`, null, { params }));

export const sendMissedCallNotification = (entryId) =>
  extractData(apiClient.post(`/missed-calls/${entryId}/notify`));

export const fetchCallFilterOptions = (params) =>
  extractData(apiClient.get('/analytics/calls/filter-options', { params }));

export const startCallsExport = (params, force = false) =>
  extractData(apiClient.post('/analytics/calls/export', null, {
    params: { ...params, force }
  }));

export const listCallsExportJobs = () =>
  extractData(apiClient.get('/analytics/calls/export/jobs'));

export const getCallsExportJob = (id) =>
  extractData(apiClient.get(`/analytics/calls/export/jobs/${id}`));

export const downloadCallsExportJob = async (id, fallbackFilename) => {
  const response = await apiClient.get(`/analytics/calls/export/jobs/${id}/download`, {
    responseType: 'blob',
    timeout: 600000
  });

  const blob = response.data;
  const filename = resolveDownloadFilename(response.headers?.['content-disposition'], fallbackFilename);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const deleteCallsExportJob = (id) =>
  extractData(apiClient.delete(`/analytics/calls/export/jobs/${id}`));

const resolveDownloadFilename = (contentDisposition, fallbackFilename) => {
  if (fallbackFilename) {
    return fallbackFilename;
  }

  const utf8Match = contentDisposition?.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const plainMatch = contentDisposition?.match(/filename="?([^";]+)"?/i);
  if (plainMatch?.[1]) {
    return plainMatch[1];
  }

  return 'calls-export.xlsx';
};

export const fetchComplaints = (params) =>
  extractData(apiClient.get('/analytics/complaints', { params }));

export const fetchComplaintsSummary = (params) =>
  extractData(apiClient.get('/analytics/complaints/summary', { params }));

export const fetchComplaintsPoints = (params) =>
  extractData(apiClient.get('/analytics/complaints/points', { params }));

export const fetchComplaintsOptions = () =>
  extractData(apiClient.get('/analytics/complaints/options'));

export const fetchComplaintCase = (phone, date) =>
  extractData(apiClient.get(`/analytics/complaints/${encodeURIComponent(phone)}/${date}`));

export const updateComplaintReview = (phone, date, payload) =>
  extractData(apiClient.put(`/analytics/complaints/${encodeURIComponent(phone)}/${date}/review`, payload));

export const fetchComplaintHistory = (phone, date) =>
  extractData(apiClient.get(`/analytics/complaints/${encodeURIComponent(phone)}/${date}/history`));

export const startComplaintsExport = (params, force = false) =>
  extractData(apiClient.post('/analytics/complaints/export', null, {
    params: { ...params, force }
  }));

export const listComplaintsExportJobs = () =>
  extractData(apiClient.get('/analytics/complaints/export/jobs'));

export const getComplaintsExportJob = (id) =>
  extractData(apiClient.get(`/analytics/complaints/export/jobs/${id}`));

export const downloadComplaintsExportJob = async (id, fallbackFilename) => {
  const response = await apiClient.get(`/analytics/complaints/export/jobs/${id}/download`, {
    responseType: 'blob',
    timeout: 600000
  });

  const blob = response.data;
  const filename = resolveDownloadFilename(response.headers?.['content-disposition'], fallbackFilename);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const deleteComplaintsExportJob = (id) =>
  extractData(apiClient.delete(`/analytics/complaints/export/jobs/${id}`));

export const startComplaintsPointsRefresh = (params, force = false) =>
  extractData(apiClient.post('/analytics/complaints/points-refresh', null, {
    params: { ...params, force }
  }));

export const listComplaintsPointsRefreshJobs = () =>
  extractData(apiClient.get('/analytics/complaints/points-refresh/jobs'));

export const getComplaintsPointsRefreshJob = (id) =>
  extractData(apiClient.get(`/analytics/complaints/points-refresh/jobs/${id}`));

export const deleteComplaintsPointsRefreshJob = (id) =>
  extractData(apiClient.delete(`/analytics/complaints/points-refresh/jobs/${id}`));

export const fetchClientsStats = (params) =>
  extractData(apiClient.get('/clients/stats', { params }));

export const fetchClients = (params) =>
  extractData(apiClient.get('/clients', { params }));

export const createClient = (payload) =>
  extractData(apiClient.post('/clients', payload));

export const createEmployee = (payload) =>
  extractData(apiClient.post('/employees', payload));

export const updateEmployee = (id, payload) =>
  extractData(apiClient.put(`/employees/${id}`, payload));

// Точечный тумблер участия сотрудника в анализе. Отдельно от updateEmployee, который шлёт
// карточку целиком: иначе переключатель утащил бы за собой несохранённый черновик
// имени и отдела из той же строки таблицы.
export const setEmployeeAnalysisEnabled = (id, enabled) =>
  extractData(apiClient.put(`/employees/${id}/analysis`, { enabled }));

export const updateClient = (id, payload) =>
  extractData(apiClient.put(`/clients/${id}`, payload));

export const deleteClient = (id) =>
  extractData(apiClient.delete(`/clients/${id}`));

export const fetchAnalyticsClientsStats = (params) =>
  extractData(apiClient.get('/analytics/clients/stats', { params }));

export const fetchAnalyticsClients = (params) =>
  extractData(apiClient.get('/analytics/clients', { params }));

export const fetchClientSummary = (clientId, params) =>
  extractData(apiClient.get(`/analytics/clients/${clientId}/summary`, { params }));

export const fetchTasks = (params) =>
  extractData(
    apiClient.get('/tasks', {
      params,
      timeout: TASKS_SEARCH_TIMEOUT_MS
    })
  );

export const fetchTasksSummary = (params) =>
  extractData(apiClient.get('/tasks/summary', { params }));

export const fetchTaskStatus = (taskId) =>
  extractData(apiClient.get(`/tasks/${taskId}/status`));

// scope: 'biometry' — заново определить сотрудника по голосу без повторного STT и LLM.
export const restartTask = (taskId, { fromCheckpoint = true, scope } = {}) =>
  extractData(
    apiClient.post(`/tasks/${taskId}/restart`, null, {
      params: scope ? { fromCheckpoint, scope } : { fromCheckpoint }
    })
  );

export const deleteTask = (taskId) =>
  extractData(apiClient.delete(`/tasks/${taskId}`));

export const bulkRestartTasks = (filterParams, { fromCheckpoint = false, scope } = {}) =>
  extractData(
    apiClient.post('/tasks/bulk-restart', null, {
      params: scope ? { ...filterParams, fromCheckpoint, scope } : { ...filterParams, fromCheckpoint },
      timeout: TASKS_SEARCH_TIMEOUT_MS
    })
  );

export const downloadTasksArtifacts = (filterParams) =>
  apiClient
    .get('/tasks/bulk-artifacts', {
      params: filterParams,
      responseType: 'blob',
      timeout: 600000
    })
    .then((response) => response.data);

export const createAnalyticalTasks = ({
  files,
  employeeId,
  clientId,
  callStartedAtUtc,
  callEndedAtUtc,
  durationSeconds,
  callDirection,
  topic,
  notes
}) => {
  const formData = new FormData();
  const payloadFiles = Array.isArray(files) ? files : files ? [files] : [];

  payloadFiles.forEach((item) => {
    if (item) {
      formData.append('files', item);
    }
  });

  if (payloadFiles.length === 1) {
    formData.append('file', payloadFiles[0]);
  }

  if (employeeId) {
    formData.append('employeeId', employeeId);
  }
  if (clientId) {
    formData.append('clientId', clientId);
  }
  if (callStartedAtUtc) {
    formData.append('callStartedAtUtc', callStartedAtUtc);
  }
  if (callEndedAtUtc) {
    formData.append('callEndedAtUtc', callEndedAtUtc);
  }
  if (durationSeconds) {
    formData.append('durationSeconds', durationSeconds);
  }
  if (callDirection) {
    formData.append('callDirection', callDirection);
  }
  if (topic) {
    formData.append('topic', topic);
  }
  if (notes) {
    formData.append('notes', notes);
  }

  return extractData(
    apiClient.post('/tasks', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  );
};

export const fetchTaskTranscript = (taskId) =>
  apiClient
    .get(`/tasks/${taskId}/transcript`, { responseType: 'text' })
    .then((response) => response.data);

export const fetchTaskAnalysis = (taskId) =>
  extractData(apiClient.get(`/tasks/${taskId}/analysis`));

export const getTaskAudioUrl = (taskId, { download = false } = {}) =>
  buildTaskAudioUrl(apiClient.defaults.baseURL, taskId, { download });

export const cleanupStorage = (payload) =>
  extractData(apiClient.post('/maintenance/storage/cleanup', payload));

export const fetchTimeZoneSetting = () =>
  extractData(apiClient.get('/settings/timezone'));

export const updateTimeZoneSetting = (timeZone) =>
  apiClient.put('/settings/timezone', { timeZone });

export const fetchFeatures = () =>
  extractData(apiClient.get('/settings/features'));

export const updateIikoFeature = (enabled) =>
  extractData(apiClient.put('/settings/features/iiko', { enabled }));

export const updateTranscriptsFeature = (visible) =>
  extractData(apiClient.put('/settings/features/transcripts', { visible }));

// Анализ внутренних звонков — только на установке с Asterisk (иначе сервер отвечает 409).
export const updateInternalCallsFeature = (analyze) =>
  extractData(apiClient.put('/settings/features/internal-calls', { analyze }));

// Биометрия оператора: подключение к сервису определения сотрудника по голосу (только админ).
// Токен сервер не отдаёт — только hasToken и последние символы; пустой token при сохранении
// оставляет сохранённый.
export const fetchBiometrySettings = () =>
  extractData(apiClient.get('/settings/biometry'));

export const updateBiometrySettings = (payload) =>
  extractData(apiClient.put('/settings/biometry', payload));

export const checkBiometrySettings = (payload) =>
  extractData(apiClient.post('/settings/biometry/check', payload, { timeout: 30000 }));

export const fetchProcessingState = () =>
  extractData(apiClient.get('/processing/state'));

export const startProcessing = () =>
  extractData(apiClient.post('/processing/start'));

export const stopProcessing = (scope) =>
  extractData(apiClient.post('/processing/stop', { scope }));

export const updateProcessingQuota = ({ minutesLimit, periodStartUtc, autoStopScope }) =>
  extractData(apiClient.put('/processing/quota', { minutesLimit, periodStartUtc, autoStopScope }));

// Приём звонков от телефоний за сутки: отказы вебхуков, упавшие записи, звонки без записи (только админ).
export const fetchTelephonyIntakeHealth = () =>
  extractData(apiClient.get('/integrations/telephony-health'));

// Телефонии, у которых на сервере заданы креды и есть список сотрудников (Mango, UIS).
export const fetchTelephonyEmployeeSources = () =>
  extractData(apiClient.get('/employees/telephony-sources'));

// Подсказки имён для существующих карточек; без source — от всех настроенных телефоний.
// Заменил fetch-from-mango: серверный маршрут остался для совместимости, интерфейс в него не ходит.
export const fetchEmployeesFromTelephony = (source) =>
  extractData(apiClient.post('/employees/fetch-from-telephony', null, source ? { params: { source } } : undefined));

export const fetchUsers = () =>
  extractData(apiClient.get('/users'));

export const createUser = (payload) =>
  extractData(apiClient.post('/users', payload));

export const updateUser = (id, payload) =>
  extractData(apiClient.put(`/users/${id}`, payload));

export const changeUserPassword = (id, password) =>
  extractData(apiClient.post(`/users/${id}/password`, { password }));

export const deactivateUser = (id) =>
  extractData(apiClient.delete(`/users/${id}`));

// ── Пакеты нод LLM-пайплайна ───────────────────────────────────────────────

export const fetchNodePacks = () =>
  extractData(apiClient.get('/llm-node-packs'));

export const fetchActiveNodePack = () =>
  extractData(apiClient.get('/llm-node-packs/active'));

export const fetchNodePack = (id) =>
  extractData(apiClient.get(`/llm-node-packs/${id}`));

export const uploadNodePack = (file) => {
  const formData = new FormData();
  formData.append('file', file);

  return extractData(apiClient.post('/llm-node-packs/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000
  }));
};

export const activateNodePack = (id) =>
  extractData(apiClient.post(`/llm-node-packs/${id}/activate`));

export const deactivateNodePack = () =>
  extractData(apiClient.post('/llm-node-packs/deactivate'));

export const deleteNodePack = (id) =>
  apiClient.delete(`/llm-node-packs/${id}`);

export const updateNodePackComplaintFlag = (id, path) =>
  apiClient.put(`/llm-node-packs/${id}/complaint-flag`, { path });

export const startNodeRecalculation = (payload) =>
  extractData(apiClient.post('/llm-node-packs/recalculate', payload));

export const listNodeRecalculationJobs = () =>
  extractData(apiClient.get('/llm-node-packs/recalculate/jobs'));

export const deleteNodeRecalculationJob = (id) =>
  apiClient.delete(`/llm-node-packs/recalculate/jobs/${id}`);

// ── Демо-данные ──────────────────────────────────────────────────────────────

/** Шаблон файла демо-данных (ZIP: схема строки, пример, памятка) под активный пакет узлов. */
export const downloadDemoDataTemplate = async () => {
  const response = await apiClient.get('/settings/demo-data/template', {
    responseType: 'blob',
    timeout: 120000
  });

  const filename = resolveDownloadFilename(response.headers?.['content-disposition'], null) || 'demo-data-template.zip';
  const url = URL.createObjectURL(response.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const postDemoDataFile = (path, file) => {
  const formData = new FormData();
  formData.append('file', file);

  return extractData(apiClient.post(path, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000
  }));
};

export const validateDemoData = (file) => postDemoDataFile('/settings/demo-data/validate', file);

export const importDemoData = (file) => postDemoDataFile('/settings/demo-data/import', file);

export const listDemoImportJobs = () =>
  extractData(apiClient.get('/settings/demo-data/jobs'));

export const deleteDemoImportJob = (id) =>
  apiClient.delete(`/settings/demo-data/jobs/${id}`);

/**
 * Показать или скрыть карточку дашборда. Ключ `{node_id}.{output_key}` стабилен между версиями
 * пакета, поэтому настройка переживает его перезаливку. Сервер мержит по одной карточке и
 * возвращает состояние целиком.
 */
export const updateNodeChartVisibility = (chartId, visible) =>
  extractData(apiClient.put('/settings/node-charts/visibility', { chartId, visible }));

/**
 * Переименования подписей пакета для интерфейса — сам пакет не меняется. `null` или пустая
 * строка возвращает подпись пакета. Сервер мержит по одной записи и возвращает все
 * переименования тенанта `{ charts, values, nodes }`.
 */
export const updateNodeChartTitle = (chartId, title) =>
  extractData(apiClient.put('/settings/node-labels/chart', { chartId, title }));

export const updateNodeValueLabel = (chartId, code, label) =>
  extractData(apiClient.put('/settings/node-labels/value', { chartId, code, label }));

/**
 * Показ одного значения на графике карточки. Скрытое значение уходит со столбцов и из
 * «применимых звонков», но остаётся в фильтрах и журнале. Пишется в ту же запись, что и
 * переименования, и ответ несёт её целиком.
 */
export const updateNodeValueVisibility = (chartId, code, visible) =>
  extractData(apiClient.put('/settings/node-labels/value-visibility', { chartId, code, visible }));

/**
 * Порядок пунктов чек-листа на экране — полный список ключей в новом порядке. `null` возвращает
 * порядок пакета. Пишется в ту же запись, что и переименования, и ответ несёт её целиком.
 */
export const updateNodeItemOrder = (chartId, itemKeys) =>
  extractData(apiClient.put('/settings/node-labels/item-order', { chartId, itemKeys }));

export const fetchUiPanelSettings = () =>
  extractData(apiClient.get('/settings/ui-panels'));

export const updateUiPanelLabels = (panelId, title, description) =>
  extractData(apiClient.put('/settings/ui-panels/labels', { panelId, title, description }));

export const updateUiPanelVisibility = (panelId, visible) =>
  extractData(apiClient.put('/settings/ui-panels/visibility', { panelId, visible }));

/** `items: null` возвращает вкладке раскладку по умолчанию. */
export const updateUiPanelLayout = (tab, columns, items, expectedRevision) =>
  extractData(apiClient.put('/settings/ui-panels/layout', { tab, columns, items, expectedRevision }));

export const updateNodeFormTitle = (nodeId, title) =>
  extractData(apiClient.put('/settings/node-labels/form', { nodeId, title }));

// ── Поля «Услуга» и «Итог звонка» карточки звонка ──────────────────────────
// Каждый PUT меняет один аспект одного поля (slot — service | outcome) и возвращает состояние
// целиком, как GET: источник, название и видимость правятся независимо.

export const fetchCallCardFields = () =>
  extractData(apiClient.get('/settings/call-card-fields'));

/** Путь выхода пакета (`node.output` или `node.output.flag`); `null` — источник по умолчанию. */
export const updateCallCardFieldSource = (slot, source) =>
  extractData(apiClient.put(`/settings/call-card-fields/${slot}/source`, { source }));

/** Своё название поля; пустое — название по умолчанию. */
export const updateCallCardFieldTitle = (slot, title) =>
  extractData(apiClient.put(`/settings/call-card-fields/${slot}/title`, { title }));

export const updateCallCardFieldVisibility = (slot, visible) =>
  extractData(apiClient.put(`/settings/call-card-fields/${slot}/visibility`, { visible }));

// ── Динамическая аналитика по нодам ────────────────────────────────────────

export const fetchNodeCatalog = () =>
  extractData(apiClient.get('/analytics/nodes/catalog'));

export const fetchNodeAggregate = (params) =>
  extractData(apiClient.get('/analytics/nodes/aggregate', { params }));

export const fetchNodeExamples = (params) =>
  extractData(apiClient.get('/analytics/nodes/examples', { params }));

/** Динамика по периодам: те же параметры, что у агрегата, плюс granularity (day|week|month). */
export const fetchNodeTimeseries = (params) =>
  extractData(apiClient.get('/analytics/nodes/timeseries', { params }));

/** Тепловая карта чек-листа: пункт × сотрудник. */
export const fetchNodeHeatmap = (params) =>
  extractData(apiClient.get('/analytics/nodes/heatmap', { params }));

/** Разбор по сотрудникам раздела «Типы и итоги звонков»: срез, знаменатель и баллы по формам. */
export const fetchEmployeeBreakdown = (params) =>
  extractData(apiClient.get('/analytics/nodes/employee-breakdown', { params }));
